import React from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import styles from './AssignmentReportModal.module.scss';

export interface ReportFailure {
  carrierPolicyNumber: string;
  reason: string;
  hint: string;
}

export interface ReportAssignee {
  name: string;
  initials: string;
}

interface Props {
  open: boolean;
  assignedCarrierNumbers: string[];
  failed: ReportFailure[];
  assignee: ReportAssignee;
  assignmentTypeLabel: string;
  onClose: () => void;
}

function cn(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

export function AssignmentReportModal({
  open,
  assignedCarrierNumbers,
  failed,
  assignee,
  assignmentTypeLabel,
  onClose,
}: Props) {
  const allRows = [
    ...failed.map(f => ({ type: 'failed' as const, carrierPolicyNumber: f.carrierPolicyNumber, reason: f.reason, hint: f.hint })),
    ...assignedCarrierNumbers.map(c => ({ type: 'assigned' as const, carrierPolicyNumber: c })),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 720 } }}
    >
      <DialogTitle sx={{ padding: '10px 16px 6px' }}>
        <Typography component="div" className={styles.title}>
          Assignment report
        </Typography>
        <Typography component="div" className={styles.subtitle}>
          {assignee.name} · {assignmentTypeLabel}
        </Typography>
      </DialogTitle>

      <Box className={styles.summaryBar}>
        <Typography variant="caption" className={styles.summaryStat}>
          <strong>{assignedCarrierNumbers.length}</strong> assigned
        </Typography>
        {failed.length > 0 && (
          <Typography variant="caption" className={styles.summaryStat}>
            <strong>{failed.length}</strong> failed
          </Typography>
        )}
      </Box>

      <Box className={styles.columnHeaders}>
        <Typography variant="caption" className={styles.columnHeaderText}>
          Policy ID
        </Typography>
        <Typography variant="caption" className={styles.columnHeaderText}>
          Details
        </Typography>
        <Typography
          variant="caption"
          className={styles.columnHeaderText}
          sx={{ textAlign: 'right' }}
        >
          Status
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 400 }}>
        {allRows.map((row, idx) => {
          const isLast = idx === allRows.length - 1;
          if (row.type === 'failed') {
            return (
              <Box
                key={`failed-${row.carrierPolicyNumber}`}
                className={cn(styles.row, styles.rowFailed, isLast && styles.rowLast)}
              >
                <Typography variant="body2" className={styles.policyId}>
                  {row.carrierPolicyNumber}
                </Typography>
                <Box>
                  <Typography className={styles.failedLabel}>
                    {row.reason}
                  </Typography>
                  <Typography variant="caption" className={styles.failedHint}>
                    {row.hint}
                  </Typography>
                </Box>
                <Chip
                  label="Failed"
                  className={cn(styles.badge, styles.badgeFailed)}
                />
              </Box>
            );
          }
          return (
            <Box
              key={`assigned-${row.carrierPolicyNumber}`}
              className={cn(styles.row, styles.rowAssigned, isLast && styles.rowLast)}
            >
              <Typography variant="body2" className={styles.policyId}>
                {row.carrierPolicyNumber}
              </Typography>
              <Box className={styles.assigneeCell}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '11px',
                    fontWeight: 500,
                    bgcolor: '#e3f2fd',
                    color: '#1565c0',
                  }}
                >
                  {assignee.initials}
                </Avatar>
                <Typography className={styles.assigneeName}>
                  {assignee.name}
                </Typography>
              </Box>
              <Chip
                label="Assigned"
                className={cn(styles.badge, styles.badgeAssigned)}
              />
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions className={styles.footer}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          className={styles.closeButton}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
